// stable version  of magic link EXCEPT for the email link takes users to the home page instead of dashboard but all authentication and email pieces are working very well at this point. May want to roll back to this spot in the future. 
import supabase from './supabaseUtils';
import { sendMagicLinkEmail } from './emailUtils';

const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;   // already defined

/**
 * Sends a magic link to the provided email address using Resend
 * @param {string} email The user's email address
 * @returns {Promise} Result of the operation
 */
export async function sendMagicLink(email) {
  const siteUrl = window.location.origin;
  const redirect = `${siteUrl}/dashboard`;  // Supabase will append ?code=


  /* Ask the edge-function – supabase client adds `apikey` automatically */
  const { data, error } = await supabase.functions.invoke('generate-link', {
    body: { email, redirect }
  });
  if (error) throw error;
  const { link } = data;

  // 2.  Send it with Resend
  await sendMagicLinkEmail(email, link);

  return { data: { message: "Magic link sent" } };
}

/**
 * Sends an SMS verification code to the provided phone number
 * @param {string} phone The user's phone number (format: +1XXXXXXXXXX)
 * @returns {Promise} Result of the OTP operation
 */
export async function sendPhoneOTP(phone) {
  // Ensure the phone number is in the correct format (+1XXXXXXXXXX)
  const formattedPhone = formatPhoneNumber(phone);
  
  return supabase.auth.signInWithOtp({
    phone: formattedPhone
  });
}

/**
 * Verifies the OTP code sent to phone
 * @param {string} phone The user's phone number
 * @param {string} token The OTP code received via SMS
 * @returns {Promise} Result of the verification
 */
export async function verifyPhoneOTP(phone, token) {
  // Ensure the phone number is in the correct format
  const formattedPhone = formatPhoneNumber(phone);
  
  return supabase.auth.verifyOtp({
    phone: formattedPhone,
    token,
    type: 'sms'
  });
}

/**
 * Format a phone number to ensure it has the international format
 * @param {string} phone Phone number to format
 * @returns {string} Formatted phone number
 */
export function formatPhoneNumber(phone) {
  // Remove any non-digit characters except the + sign at the beginning
  let formatted = phone.replace(/[^\d+]/g, '');
  
  // If the number doesn't start with +, add +1 (US) prefix
  if (!formatted.startsWith('+')) {
    // If the number starts with 1, add just the +
    if (formatted.startsWith('1')) {
      formatted = '+' + formatted;
    } else {
      // Otherwise add +1 (US/Canada code)
      formatted = '+1' + formatted;
    }
  }
  
  return formatted;
}

/**
 * Checks if the user exists in the auth system
 * @param {string} email The user's email address
 * @returns {Promise<boolean>} Whether the user exists
 */
export async function checkUserExists(email) {
  try {
    // Try to sign in - this will return differently if user exists or not
    const { error } = await supabase.auth.signInWithOtp({ 
      email,
      options: { 
        shouldCreateUser: false 
      }
    });
    
    // If there's an error with "User not found", the user doesn't exist
    if (error && error.message.includes('User not found')) {
      return false;
    }
    
    // Otherwise, assume the user exists
    return true;
  } catch (error) {
    console.error('Error checking if user exists:', error);
    return false;
  }
}

/**
 * Generates an authenticated link to any route in the application
 * The link will automatically log the user in when clicked
 * 
 * @param {string} email User's email address
 * @param {string} redirectPath Path to redirect to after authentication (e.g., '/dashboard/images')
 * @param {object} metadata Optional metadata to pass through the link
 * @returns {Promise<{success: boolean, message: string}>} Result of the operation
 */
export async function generateAuthenticatedLink(email, redirectPath = '/dashboard', metadata = {}) {
  try {
    if (!email) {
      return { 
        success: false, 
        message: 'Email is required to generate an authenticated link' 
      };
    }
    
    // Use the site URL for the redirect
    const siteUrl = window.location.origin;
    
    // Store the intended redirect path in metadata so we can use it after auth
    const enhancedMetadata = {
      ...metadata,
      redirectAfterAuth: redirectPath
    };
    
    // Send a magic link to the user's email
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: siteUrl,
        emailLinkExpirationInSeconds: 60 * 60, // 1 hour
        data: enhancedMetadata
      }
    });
    
    if (error) {
      console.error('Error generating authenticated link:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to generate authenticated link' 
      };
    }
    
    return { 
      success: true, 
      message: `A secure link has been sent to ${email} that will give you access to ${redirectPath}` 
    };
  } catch (error) {
    console.error('Unexpected error generating authenticated link:', error);
    return { 
      success: false, 
      message: 'An unexpected error occurred while generating the link' 
    };
  }
}

/**
 * Generates authentication using phone number and sends SMS verification
 * 
 * @param {string} phone User's phone number (format: +1XXXXXXXXXX or any format that will be normalized)
 * @param {object} metadata Optional metadata to include with the authentication
 * @returns {Promise<{success: boolean, message: string}>} Result of the operation
 */
export async function generatePhoneAuthentication(phone, metadata = {}) {
  try {
    if (!phone) {
      return { 
        success: false, 
        message: 'Phone number is required for SMS authentication' 
      };
    }
    
    // Format the phone number to ensure it's in the correct format
    const formattedPhone = formatPhoneNumber(phone);
    
    // Send OTP to the phone number
    const { error } = await supabase.auth.signInWithOtp({
      phone: formattedPhone,
      options: {
        data: metadata
      }
    });
    
    if (error) {
      console.error('Error generating phone authentication:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to send verification code' 
      };
    }
    
    return { 
      success: true, 
      message: `A verification code has been sent to ${formattedPhone}`,
      phone: formattedPhone
    };
  } catch (error) {
    console.error('Unexpected error in phone authentication:', error);
    return { 
      success: false, 
      message: 'An unexpected error occurred while sending the verification code' 
    };
  }
} 